import React, { useState, useCallback } from 'react';
import { Modal, Typography } from '../../';
import TagsSelector from '../../TagsSelector';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { useBulkAddTagsToResourcesMutation } from '../../../api';
import './BulkAddTagsModal.scss';

const { Text } = Typography;

const BulkAddTagsModal = ({
    open,
    onClose,
    selectedResources = [],
    userOrg,
    onSuccess,
    // Configurable content
    title = 'Add Tags to Selected Resource(s)',
    resourceType = 'resource',
    includeMoveGroupInfo = false,
    updateTagsByQueryParameter = false,
    // Configurable API endpoints
    apiConfig = {
        getResourceEndpoint: (resourceId) => `/api/v3/sources/${resourceId}`,
        updateResourceEndpoint: (resourceId) => `/api/v3/sources/${resourceId}`,
        updateMethod: 'PUT',
    },
}) => {
    const [tags, setTags] = useState([]);
    const [newTags, setNewTags] = useState([]); // Track which tags are new
    const { notifySuccess, notifyError } = useNotificationContext();
    const [bulkAddTags, { isLoading }] = useBulkAddTagsToResourcesMutation();

    const handleTagsChange = useCallback((allTags, newTagsList) => {
        setTags(allTags);
        setNewTags(newTagsList || []);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (tags.length === 0) {
            notifyError({ description: 'Please add at least one tag.' });
            return;
        }

        try {
            await bulkAddTags({
                resources: selectedResources,
                tags,
                newTags, // Pass which tags are new
                orgId: userOrg, // Need orgId for creating new tags
                getResourceEndpoint: apiConfig.getResourceEndpoint,
                updateResourceEndpoint: apiConfig.updateResourceEndpoint,
                updateMethod: apiConfig.updateMethod,
                includeMoveGroupInfo,
                updateTagsByQueryParameter,
            }).unwrap();

            notifySuccess({
                description: `Successfully added ${tags.length} tag${tags.length > 1 ? 's' : ''} to ${selectedResources.length} ${resourceType}${selectedResources.length > 1 ? 's' : ''}.`,
            });
            setTags([]);
            setNewTags([]);
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error adding tags:', error);
            notifyError({ description: 'Failed to add tags. Please try again.' });
        }
    }, [
        tags,
        newTags,
        selectedResources,
        userOrg,
        notifySuccess,
        notifyError,
        onSuccess,
        onClose,
        bulkAddTags,
        apiConfig,
        resourceType,
    ]);

    const handleCancel = useCallback(() => {
        setTags([]);
        setNewTags([]);
        onClose();
    }, [onClose]);

    return (
        <Modal
            title={title}
            open={open}
            onOk={handleConfirm}
            onCancel={handleCancel}
            okText="Confirm"
            cancelText="Cancel"
            confirmLoading={isLoading}
            className="add-tags-modal"
            width={500}
        >
            <div className="tags-modal-content">
                <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
                    Add tags to {selectedResources.length} selected {resourceType}
                    {selectedResources.length > 1 ? 's' : ''}
                </Text>

                <TagsSelector
                    value={tags}
                    onChange={handleTagsChange}
                    placeholder="Enter tag name"
                    disabled={isLoading}
                />
            </div>
        </Modal>
    );
};

export default BulkAddTagsModal;
